import { memo, useEffect, useState } from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import { CustomTextInput } from "./inputFields/customTextInput";
import { CustomSingleSelect } from "./inputFields/customSingleSelect";
import { CustomMultiSelect } from "./inputFields/customMultiSelect";
import { CustomAutoComplete } from "./inputFields/customAutoComplete";
import { CustomMulitLineTextField } from "./inputFields/customMultiLineTextFIeld";
import { YearBasedInput } from "./inputFields/yearBasedInput";

export type FieldType =
  | "text"
  | "number"
  | "date"
  | "multiline"
  | "select"
  | "multi-select"
  | "autocomplete"
  | "checkbox"
  | "year-based"
  | "multiOption"
  | "Borrowers";

export interface FormFieldConfig {
  field: string;
  label: string;
  type: FieldType;
  optionLabelKey?: string;
  optionsKey?: string;
  formatNumber?: boolean;
  multiline?: boolean;
  isStringArray?: boolean;
}

interface FormFieldProps {
  config: FormFieldConfig;
  value: any;
  onChange: (field: string, value: any) => void;
  optionsMap?: Record<string, any[]>;
  required?: boolean;
}

const EMPTY_OPTIONS_MAP: NonNullable<FormFieldProps["optionsMap"]> = {};

/**
 * Memoized: pass a stable `onChange` and `optionsMap` so typing in one field
 * does not re-render every other field on the form.
 */
export const FormField = memo(function FormField({
  config,
  value,
  onChange,
  optionsMap = EMPTY_OPTIONS_MAP,
  required = false,
}: FormFieldProps) {
  const {
    field,
    label,
    type,
    optionLabelKey = "label",
    optionsKey,
    formatNumber = false,
    multiline = false,
    isStringArray = false,
  } = config;

  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  const options = optionsKey ? optionsMap[optionsKey] || [] : [];

  // Input components
  if (type === "text" || type === "number" || type === "date") {
    return (
      <CustomTextInput
        label={label}
        type={type}
        value={localValue}
        onChange={(val) => {
          // Commit on every keystroke so Save sees the edit without a blur
          setLocalValue(val);
          onChange(field, val === "" ? null : val);
        }}
        formatNumber={formatNumber}
        multiline={multiline}
        fullWidth={true}
        required={required}
      />
    );
  }

  if (type === "multiline") {
    return (
      <CustomMulitLineTextField
        label={label}
        value={localValue}
        onChange={(val) => {
          setLocalValue(val);
          onChange(field, val === "" ? null : val);
        }}
        fullWidth={true}
      />
    );
  }

  if (type === "select") {
    // DynamicForm often passes optionLabelKey (e.g. name) rather than the full option.
    // Do not use Object.values(...).includes(value): null/undefined matches the first
    // option that has a null/undefined property (e.g. blank Deal Lead → wrong user).
    const selected = (() => {
      if (value == null || value === "") return undefined;
      if (isStringArray) {
        return options.find((opt) => opt === value);
      }
      if (typeof value === "object" && value !== null && "id" in value) {
        const valueId = (value as { id: unknown }).id;
        return options.find(
          (opt) => opt.id === valueId || String(opt.id) === String(valueId),
        );
      }
      if (optionLabelKey) {
        const byLabel = options.find((opt) => opt[optionLabelKey] === value);
        if (byLabel) return byLabel;
      }
      return options.find(
        (opt) => opt.id === value || String(opt.id) === String(value),
      );
    })();

    return (
      <CustomSingleSelect
        value={isStringArray ? value || "" : selected?.id ?? ""}
        onChange={(e) => {
          if (isStringArray) {
            // For string arrays, just pass the selected string value
            onChange(field, e.target.value || null);
          } else {
            // For object arrays, find the selected object
            const eventValue = e.target.value;
            const selected = options.find(
              (opt) =>
                opt.id === eventValue || String(opt.id) === String(eventValue),
            );

            onChange(field, selected ?? null);
          }
        }}
        options={options}
        labelKey={isStringArray ? undefined : optionLabelKey}
        placeholder={`Select ${label}`}
        fullWidth={true}
        isStringArray={isStringArray}
      />
    );
  }

  if (type === "multi-select") {
    return (
      <CustomMultiSelect
        value={value?.map((v: any) => v.id) || []}
        onChange={(e) => {
          const selected = options.filter((opt) =>
            e.target.value.includes(opt.id),
          );
          onChange(field, selected);
        }}
        options={options}
        labelKey={optionLabelKey}
        placeholder={`Select ${label}`}
        fullWidth={true}
      />
    );
  }

  if (type === "autocomplete") {
    return (
      <CustomAutoComplete
        label={label}
        selectedValue={value ?? null}
        options={options}
        labelKey={optionLabelKey}
        handleSelectChange={(_e, newValue) => onChange(field, newValue)}
        fullWidth={true}
      />
    );
  }

  if (type === "checkbox") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Checkbox
          disableRipple
          checked={Boolean(value)}
          onChange={(e) => onChange(field, e.target.checked)}
          sx={{ color: "#1B7A63 !important" }}
        />
        <Typography>{label}</Typography>
      </Box>
    );
  }

  if (type === "year-based") {
    return (
      <YearBasedInput
        label={label}
        value={value ?? null}
        onChange={(val) => onChange(field, val)}
        fullWidth={true}
      />
    );
  }

  return null;
});
